import { ICustomerVehicleInvoicesHook, IDateRange } from '@models';
import { InvoiceService } from '@services';
import { useStore } from '@stores';
import { formatDateWithOffset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

interface IProps {
  registrationNumber: string | undefined;
  customerId: string | undefined;
}

export const useCustomerVehicleInvoices = ({
  registrationNumber,
  customerId,
}: IProps): ICustomerVehicleInvoicesHook => {
  const { invoiceStore } = useStore();
  const {
    loading,
    invoices,
    totalCount,
    skipCount,
    maxResultCount,
    currentPage,
    setSkipCount,
    setCurrentPage,
  } = invoiceStore;

  const [number, setNumber] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<IDateRange>();

  useEffect(() => {
    customerId &&
      invoiceStore.getAll({
        customerId,
        status,
        number,
        registrationNumber,
        startAt: dateRange && formatDateWithOffset(dateRange.startAt),
        endAt: dateRange && formatDateWithOffset(dateRange.endAt),
        maxResultCount,
        skipCount,
      });
  }, [
    invoiceStore,
    customerId,
    status,
    number,
    registrationNumber,
    dateRange,
    maxResultCount,
    skipCount,
  ]);

  useEffect(() => {
    return () => invoiceStore.resetInvoices();
  }, [invoiceStore]);

  const handlePageChange = (pagination: any) => {
    invoiceStore.setMaxResultCount(pagination.pageSize);
    invoiceStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    invoiceStore.setCurrentPage(pagination.current);
  };

  const handleNumberSearch = debounce((text: string) => {
    setNumber(text);
    setSkipCount(0);
    setCurrentPage(1);
  }, 500);

  const fetchAllForExport = useCallback(async () => {
    const result = await InvoiceService.getAll({
      customerId,
      status,
      number,
      registrationNumber,
      startAt: dateRange && formatDateWithOffset(dateRange.startAt),
      endAt: dateRange && formatDateWithOffset(dateRange.endAt),
      maxResultCount: totalCount || 10000,
      skipCount: 0,
    });
    return result.items;
  }, [customerId, status, number, registrationNumber, dateRange, totalCount]);

  return {
    loading,
    dataSource: Array.from(invoices.values()),
    totalCount,
    maxResultCount,
    handleNumberSearch,
    currentPage,
    setStatus,
    setDateRange,
    handlePageChange,
    fetchAllForExport,
  };
};
